import React, { useState } from "react";
import { LogIn, Key, Sparkles, Stethoscope, Home, Mail, CheckCircle } from "lucide-react";
import { IQDoctorLogo } from "./IQDoctorLogo";

interface LoginProps {
  onLoginSuccess: (token: string, user: { email: string; role: string; name: string }) => void;
  isSuperadminOnly?: boolean;
}

export default function Login({ onLoginSuccess, isSuperadminOnly = false }: LoginProps) {
  const [email, setEmail] = useState(isSuperadminOnly ? "dr.watheq" : "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Additive Only: Forgot Password state machinery
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [isSuperadminView, setIsSuperadminView] = useState(isSuperadminOnly);

  // EXPERT COMMENTARY: Primary Super Admin configuration positioned first to simplify high-priority emergency administrative entry
  const staffSamples = [
    { email: "mytradefuture@gmail.com", label: "الطبيب واثق عبدالخالق الجلبي (Physician)" },
    { email: "dr.watheq@clinic.iq", label: "Watheq Al-chalabi (Super Admin)" },
    { email: "dr.ali@clinic.iq", label: "دكتوره جمانة مصطفى الجلبي (طبيبة أسنان وتجميل)" },
    { email: "fatema@clinic.iq", label: "Fatema (Reception)" },
    { email: "laila@clinic.iq", label: "Laila (Reception)" },
    { email: "alia@clinic.iq", label: "Alia (Nursing)" },
    { email: "hasan@clinic.iq", label: "Hasan (Nursing)" },
    { email: "amir@clinic.iq", label: "Amir (Lab Tech)" },
    { email: "nawal@clinic.iq", label: "Nawal (Radiology Tech)" },
    { email: "kawther@clinic.iq", label: "Kawther (Pharmacist)" },
  ];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please supply a username or email address.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Authentication failed. User role matching database record not found.");
      }

      localStorage.setItem("clinic_token", data.token);
      localStorage.setItem("clinic_user", JSON.stringify(data.user));

      onLoginSuccess(data.token, data.user);
    } catch (err: any) {
      setError(err.message || "Network connection failure. Verify database is running.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      setError("الرجاء إدخال البريد الإلكتروني الخاص بك أولاً.");
      return;
    }
    setError("");
    setForgotSuccess("");
    setForgotLoading(true);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "خطأ أثناء محاولة إرسال طلب إعادة التعيين.");
      }

      setForgotSuccess(data.message || "تم إرسال الطلب بنجاح!");
      setForgotEmail("");
    } catch (err: any) {
      setError(err.message || "فشلت عملية إرسال الطلب. يرجى محاولة الاتصال بالشبكة.");
    } finally {
      setForgotLoading(false);
    }
  };

  const selectPreseededUser = (seededEmail: string) => {
    // User requested to not autofill the email when clicking on the names, letting them enter it manually
    setPassword("");
    setError("");
  };

  return (
    <div className={`min-h-screen flex flex-col justify-center items-center px-4 py-8 transition-colors duration-500 ${isSuperadminView ? 'bg-[#0B0F19]' : 'bg-[#F8FAFC]'}`}>
      {/* Clinic Brand Banner */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-transparent w-12 h-12 flex items-center justify-center rounded-full overflow-hidden">
          <IQDoctorLogo className="w-full h-full" />
        </div>
        <div>
          <h1 className={`text-lg font-bold tracking-tight transition-colors duration-500 ${isSuperadminView ? 'text-white' : 'text-[#0F172A]'}`}>IQ-doctor clinic</h1>
          <p className={`text-[10px] font-semibold uppercase tracking-wider transition-colors duration-500 ${isSuperadminView ? 'text-amber-400' : 'text-[#64748B]'}`}>Baghdad, Iraq • Secure EHR Portal</p>
        </div>
      </div>

      {/* View Toggle Tabs for Superadmin Separation - Removed for standalone separated login pages */}

      {/* Guest Back Link to Visitor Home (Clinical Access Navigation) */}
      <div className="mb-6 text-center">
        <a
          href="#"
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition-all active:scale-[97%] cursor-pointer ${
            isSuperadminView 
              ? 'text-slate-300 hover:text-white bg-slate-800 border border-slate-705' 
              : 'text-indigo-600 hover:text-indigo-700 bg-indigo-50/60 hover:bg-indigo-50 border border-indigo-100/80'
          }`}
          dir="rtl"
        >
          <Home className="w-3.5 h-3.5" />
          <span>الرجوع إلى الصفحة الرئيسية للزوار</span>
        </a>
      </div>

      <div className={`w-full max-w-md rounded-xl transition-all duration-500 shadow-lg border p-6 animate-fade-in ${
        isSuperadminView 
          ? 'bg-[#131B2E] border-slate-800' 
          : 'bg-white border-[#E2E8F0]'
      }`}>
        {!showForgot ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              {isSuperadminView && <Key className="w-4 h-4 text-amber-400 animate-pulse" />}
              <h2 className={`text-base font-bold transition-colors ${isSuperadminView ? 'text-white' : 'text-[#1E293B]'}`}>
                {isSuperadminView ? "Superadmin Security Console" : "Staff Access Console"}
              </h2>
            </div>
            <p className={`text-xs mb-6 transition-colors ${isSuperadminView ? 'text-slate-400' : 'text-[#64748B]'}`}>
              {isSuperadminView 
                ? "Elevated administrative privilege portal with complete audit logs override." 
                : "Verify your credentials below matching Firebase SQL rules."}
            </p>

            {error && (
              <div className={`mb-4 border text-xs px-4 py-3 rounded-md font-medium ${
                isSuperadminView 
                  ? 'bg-rose-950/40 border-rose-900/60 text-rose-200' 
                  : 'bg-rose-50 border-rose-200 text-rose-700'
              }`}>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
              <div>
                <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1 transition-colors ${
                  isSuperadminView ? 'text-amber-400' : 'text-[#64748B]'
                }`}>
                  Clinic Username
                </label>
                <input
                  type="text"
                  placeholder="e.g. fatema"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full text-xs px-3 py-2 rounded-md outline-none duration-150 border ${
                    isSuperadminView 
                      ? 'bg-[#0B0F19] border-slate-800 focus:border-amber-400 text-white' 
                      : 'bg-white border-[#E2E8F0] focus:border-[#0F172A] text-[#1E293B]'
                  }`}
                  required
                  autoComplete="off"
                />
              </div>

              <div>
                <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1 transition-colors ${
                  isSuperadminView ? 'text-amber-400' : 'text-[#64748B]'
                }`}>
                  Security Password
                </label>
                <input
                  type="password"
                  placeholder="••••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full text-xs px-3 py-2 rounded-md outline-none duration-150 border ${
                    isSuperadminView 
                      ? 'bg-[#0B0F19] border-slate-800 focus:border-amber-400 text-white' 
                      : 'bg-white border-[#E2E8F0] focus:border-[#0F172A] text-[#1E293B]'
                  }`}
                  autoComplete="new-password"
                />
              </div>

              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgot(true);
                    setError("");
                    setForgotSuccess("");
                  }}
                  className={`text-[9px] hover:underline font-bold bg-transparent border-none cursor-pointer ${
                    isSuperadminView ? 'text-amber-400' : 'text-[#64748B]'
                  }`}
                >
                  نسيت كلمة المرور؟ اضغط لتقديم طلب استعادة
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full h-10 font-bold rounded-lg flex items-center justify-center gap-2 cursor-pointer transition select-none text-xs ${
                  isSuperadminView 
                    ? 'bg-[#EAB308] hover:bg-yellow-500 text-[#070A13] shadow-[0_0_15px_rgba(234,179,8,0.2)]' 
                    : 'bg-[#0F172A] hover:bg-slate-850 text-white'
                }`}
              >
                {loading ? (
                  <span className="animate-pulse">Authorizing Security Clearances...</span>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Sign In to Database
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="text-right" dir="rtl">
              <h2 className={`text-base font-bold mb-1 transition-colors ${isSuperadminView ? 'text-white' : 'text-[#1E293B]'}`}>طلب استعادة كلمة المرور</h2>
              <p className={`text-xs mb-6 transition-colors ${isSuperadminView ? 'text-slate-400' : 'text-[#64748B]'}`}>أدخل البريد الإلكتروني الخاص بك المسجل لإرسال طلب إعادة تعيين كلمة المرور إلى إدارة المجموعة.</p>
              
              {error && (
                <div className={`mb-4 border text-xs px-4 py-3 rounded-md font-medium text-left ${
                  isSuperadminView 
                    ? 'bg-rose-950/40 border-rose-900/60 text-rose-200' 
                    : 'bg-rose-50 border-rose-200 text-rose-700'
                }`} dir="ltr">
                  {error}
                </div>
              )}

              {forgotSuccess && (
                <div className={`mb-4 border text-xs px-4 py-3 rounded-md font-medium flex items-start gap-2 ${
                  isSuperadminView 
                    ? 'bg-emerald-950/40 border-emerald-900/60 text-emerald-200' 
                    : 'bg-[#ECFDF5] border-emerald-200 text-emerald-800'
                }`}>
                  <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{forgotSuccess}</span>
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4 text-left" dir="ltr" autoComplete="off">
                <div>
                  <label className={`block text-[11px] font-semibold uppercase tracking-wider mb-1 text-right transition-colors ${
                    isSuperadminView ? 'text-amber-400' : 'text-[#64748B]'
                  }`} dir="rtl">البريد الإلكتروني للعيادة / Clinic Email</label>
                  <input
                    type="email"
                    placeholder="name@clinic.iq"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={`w-full text-xs px-3 py-2 rounded-md outline-none duration-150 border ${
                      isSuperadminView 
                        ? 'bg-[#0B0F19] border-slate-800 focus:border-amber-400 text-white' 
                        : 'bg-white border-[#E2E8F0] focus:border-[#0F172A] text-[#1E293B]'
                    }`}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="pt-2 flex flex-col gap-2">
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md flex items-center justify-center gap-2 cursor-pointer transition select-none text-xs"
                  >
                    {forgotLoading ? (
                      <span className="animate-pulse">جاري إرسال طلب الاستعادة...</span>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        إرسال طلب الاستعادة إلى iq.doctor.gp@gmail.com
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowForgot(false);
                      setError("");
                      setForgotSuccess("");
                    }}
                    className={`w-full h-10 border font-semibold rounded-md flex items-center justify-center gap-2 cursor-pointer transition select-none text-xs ${
                      isSuperadminView 
                        ? 'border-slate-700 hover:bg-slate-800 text-slate-300' 
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    الرجوع لتسجيل الدخول / Back to Login
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className={`w-full border-t ${isSuperadminView ? 'border-slate-800' : 'border-[#E2E8F0]'}`} />
          </div>
          <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-wider">
            <span className={`px-3 transition-colors ${isSuperadminView ? 'bg-[#131B2E] text-slate-400' : 'bg-white text-[#64748B]'}`}>
              {isSuperadminView ? "Administrative Key Accounts" : "Select Seeding Quick Account"}
            </span>
          </div>
        </div>

         {/* Quick Seeding Buttons */}
        <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
          {staffUsersQuick(
            selectPreseededUser, 
            staffSamples.filter(s => isSuperadminView ? s.email === "dr.watheq@clinic.iq" : s.email !== "dr.watheq@clinic.iq"), 
            email,
            isSuperadminView
          )}
        </div>
      </div>

      <div className={`mt-8 text-center max-w-xs text-[11px] transition-colors ${isSuperadminView ? 'text-slate-500' : 'text-[#64748B]'}`}>
        <p>© 2026 IQ-doctor clinic. Restricted system for authorized physicians, receptionists, and technologist staff.</p>
      </div>
    </div>
  );
}

// Subordinate component function to generate clean button lists
function staffUsersQuick(
  onSelect: (email: string) => void,
  samples: Array<{ email: string; label: string }>,
  currentEmail: string,
  isDark?: boolean
) {
  return samples.map((employee) => {
    const username = employee.email.split("@")[0].toLowerCase();
    const isSelected = currentEmail.toLowerCase() === username || currentEmail.toLowerCase() === employee.email.toLowerCase();
    return (
      <button
        key={employee.email}
        type="button"
        onClick={() => onSelect(employee.email)}
        className={`px-3 py-2 text-left rounded-md text-xs font-semibold select-none border active:scale-[98%] cursor-pointer transition flex flex-col justify-between ${
          isSelected
            ? isDark
              ? "bg-amber-500/10 border-amber-500 text-amber-400"
              : "bg-slate-100 border-[#0F172A] text-[#0F172A]"
            : isDark
              ? "bg-[#0B0F19] hover:bg-slate-800 border-slate-800 text-slate-300"
              : "bg-[#F8FAFC] hover:bg-slate-100 border-[#E2E8F0] text-[#1E293B]"
        }`}
      >
        <span>{employee.label}</span>
      </button>
    );
  });
}
